import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/base/Avatar';
import ProgressBar from '@/components/base/ProgressBar';
import PlatformIcon from '@/components/base/PlatformIcon';
import { formatCompact, formatMoney } from '@/utils/format';
import type { CreatorPerformanceRow, ReportTrendDirection } from '@/types/portal';

interface CreatorPerformanceTableProps {
  rows: CreatorPerformanceRow[];
}

const trendMeta: Record<ReportTrendDirection, { icon: string; className: string; label: string }> = {
  up: { icon: 'ri-arrow-up-line', className: 'text-accent-700', label: 'Improving' },
  down: { icon: 'ri-arrow-down-line', className: 'text-primary-700', label: 'Declining' },
  flat: { icon: 'ri-subtract-line', className: 'text-foreground-400', label: 'Steady' },
};

export default function CreatorPerformanceTable({ rows }: CreatorPerformanceTableProps) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-user-star-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No creators match
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try another market or reporting period. Creator performance updates as work settles.
        </p>
      </div>
    );
  }

  const ranked = [...rows].sort((a, b) => b.engagementRate - a.engagementRate);

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="row-accent w-full min-w-[1080px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="px-5 py-3 font-semibold">#</th>
            <th className="px-3 py-3 font-semibold">Creator</th>
            <th className="px-3 py-3 font-semibold">Campaign</th>
            <th className="px-3 py-3 font-semibold">Reach</th>
            <th className="px-3 py-3 font-semibold">Engagement</th>
            <th className="px-3 py-3 font-semibold">On-time</th>
            <th className="px-3 py-3 font-semibold">Paid</th>
            <th className="px-5 py-3 text-right font-semibold">Trend</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((row, index) => {
            const trend = trendMeta[row.trend];
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/creators/${row.creatorId}`)}
                className="cursor-pointer border-b border-background-100 last:border-0 transition-colors hover:bg-background-100"
              >
                <td className="px-5 py-3.5">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-md text-xs font-medium ${
                      index < 3
                        ? 'bg-accent-100 text-accent-800'
                        : 'bg-background-200 text-foreground-600'
                    }`}
                  >
                    {index + 1}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <Avatar src={row.avatar} alt={row.name} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-900">{row.name}</p>
                      <p className="text-xs text-foreground-500">
                        {row.tier} ·{' '}
                        <span className="inline-flex items-center gap-1">
                          <PlatformIcon platform={row.platform} className="text-[11px]" />
                          {row.platform}
                        </span>{' '}
                        · {formatCompact(row.followers)} followers
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <p className="max-w-[190px] truncate text-sm text-foreground-700">{row.campaign}</p>
                  <p className="text-[11px] text-foreground-400">
                    {row.market} · {row.deliverables} deliverables
                  </p>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm text-foreground-900">{formatCompact(row.reach)}</p>
                </td>
                <td className="px-3 py-3.5">
                  <div className="w-32">
                    <p className="text-sm font-medium text-foreground-950">
                      {row.engagementRate.toFixed(1)}%
                    </p>
                    <ProgressBar value={row.engagementRate * 10} tone="accent" className="mt-1.5" />
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <div className="w-28">
                    <p
                      className={`text-sm ${
                        row.onTimeRate < 85 ? 'text-primary-700' : 'text-foreground-900'
                      }`}
                    >
                      {row.onTimeRate}%
                    </p>
                    <ProgressBar
                      value={row.onTimeRate}
                      tone={row.onTimeRate < 85 ? 'primary' : 'secondary'}
                      className="mt-1.5"
                    />
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm text-foreground-900">{formatMoney(row.paid, row.currency)}</p>
                  <p className="text-[11px] text-foreground-400">
                    {row.rating.toFixed(1)} rating
                  </p>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${trend.className}`}>
                    <i className={trend.icon} />
                    {trend.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}